(ns app.front.view.admin.gis.map-config
  (:require [app.domain.group.model :as group-model]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.admin.common :as admin-common]
            [app.front.view.admin.gis.common :as gis-common]
            [app.front.view.workspace.map :as workspace-map]
            [app.gis.config-model :as gis-config-model]
            [app.gis.util :as gis-util]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema-tools.core :as st]
            [schema.core :as s]))


(rf/reg-event-db ::open-config-editor
  (fn [db [_ server-id initial-data group-ids]]
    (let [trimmed-data (->> (st/required-keys gis-config-model/ArcGISMapConfig)
                            keys
                            (select-keys initial-data))]
      (->> (update trimmed-data :gis-service-config/arcgis-folder str)
           (merge {:group-ids group-ids})
           (assoc-in db [::map-config-editors server-id (:db/id initial-data :new)])))))


(rf/reg-event-db ::open-wms-config-editor
  (fn [db [_ server-id initial-data group-ids]]
    (let [trimmed-data (->> (st/required-keys gis-config-model/WMSMapConfig)
                            keys
                            (select-keys initial-data))]
      (->> trimmed-data
           (merge {:group-ids group-ids})
           (assoc-in db [::map-config-editors server-id (:db/id initial-data :new)])))))


(rf/reg-event-db ::close-config-editor
  (fn [db [_ server-id config-id]]
    (p/dissoc-in db [::map-config-editors server-id config-id])))


(rf/reg-sub ::config-in-editor
  (fn [db [_ server-id config-id]]
    (get-in db [::map-config-editors server-id config-id])))


(rf/reg-event-fx ::fetch-wms-capabilities
  (fn [{:keys [db]} [_ server-id path map-config-id]]
    {:db           (assoc-in db [::wms-capabilities server-id map-config-id] {:loading true})
     :comm/command {:command    :admin/fetch-wms-capabilities
                    :data       {:gis-server-id server-id
                                 :path          path}
                    :on-success [::fetch-wms-capabilities.success server-id map-config-id]}}))


(rf/reg-event-fx ::debounce-fetch-wms-capabilities
  (fn [{:keys [db]} [_ server-id path map-config-id]]
    {:db                (assoc-in db [::wms-capabilities server-id map-config-id] {:loading true})
     :dispatch-debounce {:id      ::wms-capabilities
                         :timeout 800
                         :action  :dispatch
                         :event   [::fetch-wms-capabilities server-id path map-config-id]}}))


(rf/reg-event-db ::fetch-wms-capabilities.success
  (fn [db [_ server-id map-config-id _status response _request-id]]
    (-> db
        (assoc-in
          [::wms-capabilities server-id map-config-id]
          response)
        (assoc-in
          [::map-config-editors server-id map-config-id :gis-map-config/wms-version]
          (:service-version response))
        (update-in
          [::map-config-editors server-id map-config-id :gis-map-config/wms-layers]
          (fn [old-val]
            (if (:error response)
              #{}
              (set/intersection old-val
                                (into #{} (map :name (:layers response))))))))))


(rf/reg-sub ::wms-capabilities
  (fn [db [_ server-id map-config-id]]
    (get-in db [::wms-capabilities server-id map-config-id])))


(def arcgis-map-config-checker (s/checker (merge gis-config-model/ArcGISMapConfig
                                                 {:group-ids #{s/Int}})))


(def wms-map-config-checker (s/checker (merge gis-config-model/WMSMapConfig
                                              {:group-ids #{s/Int}})))


(defn get-map-center [map-config domain-groups]
  (->> domain-groups
       (filter #((set (:group-ids map-config)) (:db/id %)))
       (mapcat :entity/_parent)
       (mapcat group-model/scope-municipalities)
       (some :municipality/center-location)))


(defn arcgis-map-config-editor [tr {server-id :db/id :as server-config} map-config-id]
  (let [server-info        (<sub [::gis-common/arcgis-server-info server-id])
        folders            (->> server-info
                                keys
                                sort)
        config             (<sub [::config-in-editor server-id map-config-id])
        selected-folder    (:gis-service-config/arcgis-folder config)
        all-services       (when selected-folder
                             (get server-info selected-folder))
        services           (->> all-services
                                (filter #(-> % :gis-service-config/arcgis-service-type (= "MapServer"))))
        selected-service   (-> (select-keys config [:gis-service-config/arcgis-service
                                                    :gis-service-config/arcgis-service-type])
                               not-empty)
        {:keys [layers
                extent]}   (<sub [::gis-common/arcgis-service-info server-id selected-folder selected-service])
        is-invalid-config? (boolean (arcgis-map-config-checker config))
        domain-groups      (<sub [::admin-common/domain-groups-of-authorities])
        set-editor-val     (fn [m]
                             (>evt [::gis-common/set-editor-value
                                    ::map-config-editors
                                    server-id
                                    map-config-id
                                    m]))
        selected-layers    (-> config :gis-map-config/arcgis-layer-ids set)
        map-center         (get-map-center config domain-groups)]
    [:div.mt-4.p-4.bg-ui-white.rounded
     [:div.w-full.lg:w-1-2
      [:div
       [ui/text-input {:label-text (tr [:gis-map-config/name])
                       :value      (:gis-map-config/name config)
                       :on-change  #(set-editor-val {:gis-map-config/name (futil/tval %)})}]]
      [:div.mt-3
       (cc/checkbox {:id         (str/join "-" [server-id map-config-id "default-enabled"])
                     :label-text (tr [:gis-map-config/default-enabled?])
                     :checked    (boolean (:gis-map-config/default-enabled? config))
                     :on-change  #(set-editor-val
                                    {:gis-map-config/default-enabled? %})})]

      [:div.w-full.lg:w-3-4.mt-3
       [:h6.mb-2 (tr [:gis-editor/groups-using-config])]
       [admin-common/member-group-editor {:tr                tr
                                          :groups            domain-groups
                                          :current-group-ids (:group-ids config)
                                          :add-title         (tr [:gis-editor/add-group])
                                          :save-evt          [::gis-common/add-domain-group
                                                              ::map-config-editors
                                                              server-id
                                                              map-config-id]
                                          :remove-evt        [::gis-common/remove-domain-group
                                                              ::map-config-editors
                                                              server-id
                                                              map-config-id]}]]

      (when-not (gis-common/system-of-type? server-config :azimut-arcgis)
        [:div.mt-4
         [:h6.mb-3 (tr [:gis-editor/folder-service])]
         (if (seq folders)
           [ui-select/select {:id          (str/join "-" [server-id map-config-id "folder-select"])
                              :class-name  "full-width-select"
                              :select-text (tr [:gis-editor/select-folder])
                              :label-text  (tr [:gis-service-config/arcgis-folder])
                              :value       selected-folder
                              :on-change   (fn [v]
                                             (set-editor-val {:gis-service-config/arcgis-folder       v
                                                              :gis-service-config/arcgis-service      nil
                                                              :gis-service-config/arcgis-service-type nil
                                                              :gis-map-config/arcgis-layer-ids        nil})
                                             (>evt [::gis-common/fetch-arcgis-server-information
                                                    server-config
                                                    (str "/" v)
                                                    [::gis-common/arcgis-folder-info-fetched server-id v]]))
                              :val->str    (fn [v]
                                             (if (str/blank? v)
                                               (tr [:gis-service-config/no-arcgis-folder])
                                               v))
                              :values      folders}]
           (cc/select-skeleton {}))])

      (when (and selected-folder
                 (not (gis-common/system-of-type? server-config :azimut-arcgis)))
        [:div.mt-3
         (cond
           (seq services)
           [ui-select/select {:id           (str/join "-" [server-id map-config-id "service-select"])
                              :class-name   "full-width-select"
                              :select-text  (tr [:gis-editor/select-service])
                              :label-text   (tr [:gis-service-config/arcgis-service])
                              :value        selected-service
                              :on-change    (fn [{:keys [:gis-service-config/arcgis-service :gis-service-config/arcgis-service-type] :as v}]
                                              (set-editor-val {:gis-service-config/arcgis-service      arcgis-service
                                                               :gis-service-config/arcgis-service-type arcgis-service-type
                                                               :gis-map-config/arcgis-layer-ids        #{}})
                                              (>evt [::gis-common/fetch-arcgis-server-information
                                                     server-config
                                                     (str/join "/"
                                                               [""
                                                                selected-folder
                                                                arcgis-service
                                                                arcgis-service-type])
                                                     [::gis-common/arcgis-service-info-fetched server-id selected-folder v]]))
                              :val->str     #(str (:gis-service-config/arcgis-service %)
                                                  " (" (:gis-service-config/arcgis-service-type %) ")")
                              :allow-empty? false
                              :values       services}]
           (seq all-services)
           (tr [:gis-map-config/no-suitable-services])

           :else
           (cc/select-skeleton {}))])]

     (when (seq layers)
       [:<>
        [:div.mt-3.flex.flex-wrap.w-full
         [:h6.mb-3.w-full (tr [:gis-map-config/arcgis-layer-ids])]
         (for-frag [[id {:keys [name]}] (->> layers
                                             (sort-by first))]
           [:div.w-full.lg:w-1-2
            (cc/checkbox {:id         (str/join "-" [server-id map-config-id "layer" id])
                          :label-text (str name " (ID: " id ")")
                          :checked    (contains? selected-layers id)
                          :on-change  #(set-editor-val
                                         {:gis-map-config/arcgis-layer-ids ((if % conj disj) selected-layers id)})})])]
        [:div.w-full.lg:w-1-2.mt-3
         [ui/text-input {:label-text (tr [:gis-map-config/ui-title])
                         :value      (:gis-map-config/ui-title config)
                         :on-change  #(set-editor-val {:gis-map-config/ui-title (futil/tval %)})}]]])

     (when (seq extent)
       (let [{:keys [xmin ymin xmax ymax spatialReference]} extent]
         [:div.mt-3
          [:h6.mb-3.w-full (tr [:gis-map-config/full-extent])]
          [:div "[ " xmin ", " ymin "; " xmax ", " ymax " ] (EPSG:" (:latestWkid spatialReference) ")"]]))

     [:div.mt-3
      (if (and map-center selected-service)
        (let [layer-url (->> [server-id
                              selected-folder
                              (:gis-service-config/arcgis-service selected-service)
                              (:gis-service-config/arcgis-service-type selected-service)]
                             (remove #(and (string? %) (str/blank? %)))
                             (str/join "/")
                             (str "/api/command/admin/map-proxy/"))]
          [:div.w-full.h-80
           [workspace-map/workspace-map {:center [(:location.center/x map-center)
                                                  (:location.center/y map-center)]
                                         :layers [{:url    layer-url
                                                   :layers (vec selected-layers)
                                                   :type   :arcgis-rest}]}]])
        [:div.mt-6 (tr [:gis-editor/select-domain-to-show-map])])]

     [:div.flex.justify-end.mt-6
      (when (not= map-config-id :new)
        [ui/modal-confirmation-button {:tr                  tr
                                       :trigger-button-text (tr [:remove])
                                       :trigger-button-opts {:kind       :danger
                                                             :class-name "mr-2"}
                                       :on-yes-click        #(>evt [::gis-common/remove-config
                                                                    :admin/remove-map-config
                                                                    map-config-id])}
         (tr [:gis-editor/confirm-removal]
             [(:gis-map-config/name config)])])
      (cc/button {:kind       :ghost
                  :class-name "mr-2"
                  :on-click   #(>evt [::close-config-editor server-id map-config-id])}
                 (tr [:cancel]))
      (cc/button {:disabled (or is-invalid-config?
                                (<sub [::gis-common/save-in-progress?]))
                  :on-click #(>evt [::gis-common/save-config
                                    :admin/save-arcgis-map-config
                                    ::map-config-editors
                                    server-id
                                    map-config-id
                                    (assoc config :gis-map-config/full-extent extent)])}
                 (tr [:save]))]]))


(defn wms-map-config-editor [tr {server-id :db/id :as server-config} map-config-id]
  (let [set-editor-val     (fn [m]
                             (>evt [::gis-common/set-editor-value
                                    ::map-config-editors
                                    server-id
                                    map-config-id
                                    m]))
        domain-groups      (<sub [::admin-common/domain-groups-of-authorities])
        config             (<sub [::config-in-editor server-id map-config-id])
        map-center         (get-map-center config domain-groups)
        selected-layers    (-> config :gis-map-config/wms-layers set)
        capabilities       (<sub [::wms-capabilities server-id map-config-id])
        is-invalid-config? (boolean (wms-map-config-checker config))]
    [:div.mt-4.p-4.bg-ui-white.rounded
     [:div.w-full.lg:w-1-2
      [:div
       [ui/text-input {:label-text (tr [:gis-map-config/name])
                       :value      (:gis-map-config/name config)
                       :on-change  #(set-editor-val {:gis-map-config/name (futil/tval %)})}]]
      [:div.mt-3
       (cc/checkbox {:id         (str/join "-" [server-id map-config-id "default-enabled"])
                     :label-text (tr [:gis-map-config/default-enabled?])
                     :checked    (boolean (:gis-map-config/default-enabled? config))
                     :on-change  #(set-editor-val
                                    {:gis-map-config/default-enabled? %})})]

      [:div.w-full.lg:w-3-4.mt-3
       [:h6.mb-2 (tr [:gis-editor/groups-using-config])]
       [admin-common/member-group-editor {:tr                tr
                                          :groups            domain-groups
                                          :current-group-ids (:group-ids config)
                                          :add-title         (tr [:gis-editor/add-group])
                                          :save-evt          [::gis-common/add-domain-group
                                                              ::map-config-editors
                                                              server-id
                                                              map-config-id]
                                          :remove-evt        [::gis-common/remove-domain-group
                                                              ::map-config-editors
                                                              server-id
                                                              map-config-id]}]]

      [:div.mt-4
       [:h6.mb-3 (tr [:gis-map-config/wms-path-and-layers])]
       [:div
        [ui/text-input {:label-text (tr [:gis-map-config/wms-path])
                        :value      (:gis-map-config/wms-path config)
                        :on-change  #(do
                                       (set-editor-val {:gis-map-config/wms-path (futil/tval %)})
                                       (>evt [::debounce-fetch-wms-capabilities server-id (futil/tval %) map-config-id]))}]]
       [:p.pt-2.pb-0
        (str (tr [:gis-map-config/wms-full-path])
             ": "
             (gis-util/wms-wfs-service-full-path (:gis-server-config/service-url server-config)
                                                 (:gis-map-config/wms-path config)))]]]

     (let [{:keys [loading error layers service-version]} capabilities]
       [:<>
        (when service-version
          [:p.pt-2.pb-0
           (str (tr [:gis-map-config/wms-version])
                ": "
                service-version)])
        (cond
          (true? loading)
          [:div.mt-6
           (cc/inline-loading {:description (tr [:gis-map-config/wms-retrieving-capabilities])})]

          (some? error)
          [:<>
           [:p.pt-2.pb-0.font-bold.text-ui-red
            (tr [:gis-map-config/wms-capabilities-error])
            " "
            (case (:status error)
              401 (tr [:gis-editor/check-credentials])
              404 (tr [:gis-editor/check-path])
              (tr [:gis-editor/unknown-error]))]]

          (= [] layers)
          [:div.w-full.lg:w-1-2.mt-3
           (tr [:gis-map-config/wms-no-layers])]

          (seq layers)
          [:<>
           [:div.mt-3.flex.flex-wrap.w-full
            [:h6.mb-3.w-full (tr [:gis-map-config/wms-layers])]
            (for-frag [{:keys [name title]} (sort-by (fn [{:keys [name]}]
                                                       (str/lower-case name))
                                                     layers)]
              [:div.w-full.lg:w-1-2
               (cc/checkbox {:id         (str/join "-" [server-id map-config-id "layer" name])
                             :label-text (str name (when title (str " (" title ")")))
                             :checked    (contains? selected-layers name)
                             :on-change  #(set-editor-val
                                            {:gis-map-config/wms-layers ((if % conj disj) selected-layers name)})})])]
           [:div.w-full.lg:w-1-2.mt-3
            [ui/text-input {:label-text (tr [:gis-map-config/ui-title])
                            :value      (:gis-map-config/ui-title config)
                            :on-change  #(set-editor-val {:gis-map-config/ui-title (futil/tval %)})}]]
           [:div.mt-3
            (if (and map-center (seq selected-layers))
              (let [layer-url (gis-util/wms-proxy-url server-id (:gis-map-config/wms-path config))]
                [:div.w-full.h-80
                 [workspace-map/workspace-map {:center [(:location.center/x map-center)
                                                        (:location.center/y map-center)]
                                               :layers [{:url    layer-url
                                                         :layers (vec selected-layers)
                                                         :type   :tile-wms}]}]])
              [:div.mt-6 (tr [:gis-map-config/wms-select-domain-to-show-map])])]])])

     [:div.flex.justify-end.mt-6
      (when (not= map-config-id :new)
        [ui/modal-confirmation-button {:tr                  tr
                                       :trigger-button-text (tr [:remove])
                                       :trigger-button-opts {:kind       :danger
                                                             :class-name "mr-2"}
                                       :on-yes-click        #(>evt [::gis-common/remove-config
                                                                    :admin/remove-map-config
                                                                    map-config-id])}
         (tr [:gis-editor/confirm-removal]
             [(:gis-map-config/name config)])])
      (cc/button {:kind       :ghost
                  :class-name "mr-2"
                  :on-click   #(>evt [::close-config-editor server-id map-config-id])}
                 (tr [:cancel]))
      (cc/button {:disabled (or is-invalid-config?
                                (<sub [::gis-common/save-in-progress?]))
                  :on-click #(>evt [::gis-common/save-config
                                    :admin/save-wms-map-config
                                    ::map-config-editors
                                    server-id
                                    map-config-id
                                    config])}
                 (tr [:save]))]]))


(def arcgis-map-config-component-data
  {:get-add-new-action          (fn [server-config server-id]
                                  (fn [_]
                                    (if (gis-common/system-of-type? server-config :azimut-arcgis)
                                      (>evt [::gis-common/fetch-azimut-arcgis-information
                                             server-config
                                             ::map-config-editors])
                                      (>evt [::gis-common/fetch-arcgis-server-information
                                             server-config
                                             ""
                                             [::gis-common/arcgis-server-root-info-fetched server-id]]))
                                    (>evt [::open-config-editor
                                           server-id
                                           {:gis-map-config/default-enabled? true
                                            :gis-map-config/arcgis-layer-ids #{}}
                                           #{}])))
   :get-config-editor-component (fn [tr server-config map-config-id]
                                  [arcgis-map-config-editor tr server-config map-config-id])
   :get-edit-action             (fn [server-config server-id map-config domain-groups]
                                  (fn []
                                    (gis-common/fetch-arcgis-server-info-for-edit server-config
                                                                                  map-config)
                                    (>evt [::open-config-editor
                                           server-id
                                           map-config
                                           (set (map :db/id domain-groups))])))
   :layer-keys                  [:gis-service-config/arcgis-folder
                                 :gis-service-config/arcgis-service
                                 :gis-service-config/arcgis-service-type
                                 :gis-map-config/arcgis-layer-ids
                                 :gis-map-config/ui-title]
   :layer-list-key              :gis-map-config/arcgis-layer-ids})


(def wms-map-config-component-data
  {:get-add-new-action          (fn [_server-config server-id]
                                  (fn [_]
                                    (>evt [::fetch-wms-capabilities
                                           server-id
                                           ""
                                           :new])
                                    (>evt [::open-wms-config-editor
                                           server-id
                                           {:gis-map-config/default-enabled? true
                                            :gis-map-config/wms-layers       #{}}
                                           #{}])))
   :get-config-editor-component (fn [tr server-config map-config-id]
                                  [wms-map-config-editor tr server-config map-config-id])
   :get-edit-action             (fn [_server-config server-id map-config domain-groups]
                                  (fn [_]
                                    (>evt [::fetch-wms-capabilities
                                           server-id
                                           (:gis-map-config/wms-path map-config)
                                           (:db/id map-config)])
                                    (>evt [::open-wms-config-editor
                                           server-id
                                           map-config
                                           (set (map :db/id domain-groups))])))
   :layer-keys                  [:gis-map-config/wms-path
                                 :gis-map-config/wms-layers
                                 :gis-map-config/ui-title]
   :layer-list-key              :gis-map-config/wms-layers})


(defn map-config-list [tr server-config {:keys [get-add-new-action
                                                get-config-editor-component
                                                get-edit-action
                                                layer-keys
                                                layer-list-key]}]
  (let [server-id (:db/id server-config)]
    [:div.mt-4
     [:div.flex.justify-between.items-center.my-3
      [:h4 (tr [:gis-editor/map-configurations])]
      (cc/button {:size     :small
                  :icon     i/plus
                  :kind     :tertiary
                  :on-click (get-add-new-action server-config server-id)}
                 (tr [:gis-editor/add-new]))]

     (when (<sub [::config-in-editor server-id :new])
       (get-config-editor-component tr server-config :new))

     (for-frag [{:gis-map-config/keys [name]
                 :as                  map-config} (->> server-config
                                                       :gis-service-config/_gis-server-config
                                                       (filter (eu/is-type-fn :entity.type/gis-map-config))
                                                       (sort-by :gis-map-config/name))
                :let [domain-groups (:group/_gis-map-configs map-config)
                      map-config-id (:db/id map-config)]]
       (if (<sub [::config-in-editor server-id map-config-id])
         (get-config-editor-component tr server-config map-config-id)
         [:div.mt-4.p-4.bg-ui-white.rounded
          [:div.flex.justify-between.items-center
           [:h5.mb-2 name]
           (cc/button {:icon             i/pen
                       :size             :small
                       :kind             :tertiary
                       :icon-description ""
                       :on-click         (get-edit-action server-config server-id map-config domain-groups)}
                      (tr [:edit]))]
          [:div
           (for-frag [group domain-groups]
             [ui/tag {:type       :round
                      :color      :night-sky
                      :class-name "first:ml-0 cloudpermit--tag--round"}
              (:group/name group)])]
          [:div.flex.flex-wrap
           (for-frag [k layer-keys
                      :let [v (k map-config)]]
             [gis-common/form-item tr k (if (= k layer-list-key)
                                          (if (seq v)
                                            (->> v
                                                 sort
                                                 (str/join ", "))
                                            (tr [:gis-editor/all]))
                                          v)])]]))]))


(defn gis-map-config-list [tr server-config]
  (let [map-config-data (if (gis-common/wms-wfs-server? server-config)
                          wms-map-config-component-data
                          arcgis-map-config-component-data)]
    [map-config-list tr server-config map-config-data]))
